import { useState, useRef, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import { useAuth } from "../../context/AuthContext";
import { foodsApi } from "../../api";
import DietitianTopbar from "./DietitianTopbar";
import classes from "./FoodLibrary.module.scss";

// ── Types ─────────────────────────────────────────────────
interface FoodRow  { name: string; quantity: string; unit: string; category: string; calories: number; protein: number; fat: number; carbs: number; }
interface Food     { id: string; name: string; quantity: string; unit?: string; category?: string; cal: number; protein: number; fat: number; carb: number; usageCount?: number; }
interface HistoryRecord { id: string; fileName: string; count: number; importedBy: string; importedAt: string | null; }

type Tab = "import" | "library" | "manual";

const PAGE_SIZE = 10;

const CATEGORIES = ["Breakfast", "Lunch", "Dinner", "Snack", "Beverage", "Supplement", "Other"];

const CAT_COLORS: Record<string, { bg: string; color: string }> = {
  Breakfast:  { bg: "#FFF8E1", color: "#8B6914" },
  Lunch:      { bg: "#E8F5E9", color: "#2E7D32" },
  Dinner:     { bg: "#EDE7F6", color: "#5E35B1" },
  Snack:      { bg: "#FFF3E0", color: "#E65100" },
  Beverage:   { bg: "#E3F2FD", color: "#1565C0" },
  Supplement: { bg: "#FCE4EC", color: "#AD1457" },
  Other:      { bg: "#F5F5F5", color: "#616161" },
};

const normalise = (key: string) => key.toLowerCase().replace(/\s+/g, "");

const mapRow = (raw: Record<string, unknown>): FoodRow | null => {
  const k = Object.fromEntries(Object.entries(raw).map(([k, v]) => [normalise(k), v]));
  const name = String(k["foodname"] ?? k["name"] ?? "").trim();
  if (!name) return null;
  const rawCat = String(k["category"] ?? k["type"] ?? "").trim();
  const category = CATEGORIES.find(c => c.toLowerCase() === rawCat.toLowerCase()) ?? (rawCat || "Other");
  return {
    name,
    quantity: String(k["quantity"] ?? k["serving"] ?? "").trim(),
    unit:     String(k["unit"] ?? k["units"] ?? "").trim(),
    category,
    calories: Number(k["calories"] ?? k["cal"] ?? k["kcal"] ?? 0),
    protein:  Number(k["protein"] ?? 0),
    fat:      Number(k["fat"] ?? 0),
    carbs:    Number(k["carbs"] ?? k["carbohydrates"] ?? k["carb"] ?? 0),
  };
};

const fmtDate = (iso: string | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// ── Custom category dropdown ──────────────────────────────
interface CatDropdownProps { value: string; onChange: (v: string) => void; }
const CatDropdown = ({ value, onChange }: CatDropdownProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const options = ["All", ...CATEGORIES];

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const cc = value !== "All" ? CAT_COLORS[value] : null;

  return (
    <div ref={ref} className={classes.cat_select_wrap} onClick={() => setOpen(o => !o)}>
      {cc && <span className={classes.cat_dot} style={{ background: cc.color }} />}
      <span className={classes.cat_select_label}>{value === "All" ? "All Categories" : value}</span>
      <svg className={`${classes.cat_chevron} ${open ? classes.cat_chevron_open : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
      {open && (
        <div className={classes.cat_menu}>
          {options.map(c => {
            const occ = c !== "All" ? CAT_COLORS[c] : null;
            return (
              <button
                key={c}
                className={`${classes.cat_menu_item} ${value === c ? classes.cat_menu_item_active : ""}`}
                onClick={e => { e.stopPropagation(); onChange(c); setOpen(false); }}
              >
                {occ ? <span className={classes.cat_dot_sm} style={{ background: occ.color }} /> : <span className={classes.cat_dot_sm} style={{ background: "#ccc" }} />}
                {c === "All" ? "All Categories" : c}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Icons ─────────────────────────────────────────────────
const IcoUpload = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);
const IcoList = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
    <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
  </svg>
);
const IcoPlus = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

// ── Component ─────────────────────────────────────────────
const FoodLibrary = () => {
  const { user } = useAuth();
  const fileRef  = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<Tab>("import");

  // ── Import tab state ──────────────────────────────────
  const [rows,      setRows]      = useState<FoodRow[]>([]);
  const [fileName,  setFileName]  = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [importDone, setImportDone] = useState(false);
  const [importErr,  setImportErr]  = useState("");
  const [history,   setHistory]   = useState<HistoryRecord[]>([]);
  const [histLoading, setHistLoading] = useState(true);

  // ── Library tab state ─────────────────────────────────
  const [foods,      setFoods]      = useState<Food[]>([]);
  const [foodsLoad,  setFoodsLoad]  = useState(false);
  const [foodsErr,   setFoodsErr]   = useState("");
  const [search,     setSearch]     = useState("");
  const [catFilter,  setCatFilter]  = useState("All");
  const [page,       setPage]       = useState(1);

  // ── Kebab / edit / delete state ───────────────────────
  const [openMenu,   setOpenMenu]   = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ top: number; right: number } | null>(null);
  const [editFood,   setEditFood]   = useState<Food | null>(null);
  const [editForm,   setEditForm]   = useState({ name: "", qty: "", unit: "g", category: "Other", cal: "", protein: "", fat: "", carb: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // ── Manual add state ──────────────────────────────────
  const emptyForm = { name: "", qty: "", unit: "g", category: "Other", calories: "", protein: "", fat: "", carbs: "" };
  const [form,       setForm]       = useState(emptyForm);
  const [saving,     setSaving]     = useState(false);
  const [saveOk,     setSaveOk]     = useState(false);
  const [saveErr,    setSaveErr]    = useState("");

  // ── Load history ──────────────────────────────────────
  const loadHistory = useCallback(() => {
    setHistLoading(true);
    foodsApi.listHistory()
      .then(d => setHistory(d.history ?? []))
      .catch(() => {})
      .finally(() => setHistLoading(false));
  }, []);

  // ── Load foods ────────────────────────────────────────
  const loadFoods = useCallback(() => {
    setFoodsLoad(true);
    setFoodsErr("");
    foodsApi.list()
      .then(d => setFoods(d.foods ?? []))
      .catch(() => setFoodsErr("Failed to load food library."))
      .finally(() => setFoodsLoad(false));
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { if (tab === "library") loadFoods(); }, [tab, loadFoods]);
  useEffect(() => {
    if (!openMenu) return;
    const close = () => { setOpenMenu(null); setMenuAnchor(null); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openMenu]);

  // ── Import handlers ───────────────────────────────────
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setImportDone(false);
    setImportErr("");
    setProgress(0);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb  = XLSX.read(ev.target?.result, { type: "array" });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws);
        const parsed = raw.map(mapRow).filter(Boolean) as FoodRow[];
        setRows(parsed);
        if (parsed.length === 0) setImportErr("No valid rows found. Check column headers.");
      } catch { setImportErr("Failed to parse file. Make sure it's a valid .xlsx or .xls file."); }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleUpload = async () => {
    if (!rows.length) return;
    setUploading(true);
    setImportErr("");
    setProgress(0);
    let succeeded = 0;
    for (const row of rows) {
      try {
        await foodsApi.add({ name: row.name, quantity: row.quantity, unit: row.unit, category: row.category, cal: row.calories, protein: row.protein, fat: row.fat, carb: row.carbs });
        succeeded++;
        setProgress(Math.round((succeeded / rows.length) * 100));
      } catch { /* skip failed rows */ }
    }
    await foodsApi.saveHistory({ fileName, count: succeeded, importedBy: user?.name ?? "Dietitian" });
    setUploading(false);
    setImportDone(true);
    setRows([]);
    setFileName("");
    if (fileRef.current) fileRef.current.value = "";
    loadHistory();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const dt = new DataTransfer();
    dt.items.add(file);
    if (fileRef.current) { fileRef.current.files = dt.files; fileRef.current.dispatchEvent(new Event("change", { bubbles: true })); }
  };

  const handleClear = () => {
    setRows([]); setFileName(""); setImportErr(""); setImportDone(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  // ── Library pagination ────────────────────────────────
  const filtered = foods.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase()) &&
    (catFilter === "All" || (f.category ?? "Other") === catFilter)
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const handleSearch  = (v: string) => { setSearch(v);    setPage(1); };
  const handleCatFilter = (v: string) => { setCatFilter(v); setPage(1); };

  // ── Manual add handler ────────────────────────────────
  const handleManualAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setSaveErr("");
    setSaveOk(false);
    try {
      await foodsApi.add({ name: form.name.trim(), quantity: form.qty, unit: form.unit, category: form.category, cal: Number(form.calories) || 0, protein: Number(form.protein) || 0, fat: Number(form.fat) || 0, carb: Number(form.carbs) || 0 });
      setSaveOk(true);
      setForm(emptyForm);
    } catch { setSaveErr("Failed to save. Please try again."); }
    finally { setSaving(false); }
  };

  // ── Open edit modal ───────────────────────────────────
  const openEdit = (f: Food) => {
    const parts = f.quantity ? f.quantity.split(" ") : [];
    setEditForm({
      name:     f.name,
      qty:      parts[0] ?? "",
      unit:     f.unit ?? parts[1] ?? "g",
      category: f.category ?? "Other",
      cal:      String(f.cal),
      protein:  String(f.protein),
      fat:      String(f.fat),
      carb:     String(f.carb),
    });
    setEditFood(f);
    setOpenMenu(null);
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editFood) return;
    setEditSaving(true);
    try {
      await foodsApi.update(editFood.id, {
        name:     editForm.name.trim(),
        quantity: editForm.qty,
        unit:     editForm.unit,
        category: editForm.category,
        cal:      Number(editForm.cal)     || 0,
        protein:  Number(editForm.protein) || 0,
        fat:      Number(editForm.fat)     || 0,
        carb:     Number(editForm.carb)    || 0,
      });
      setFoods(prev => prev.map(f => f.id === editFood.id
        ? { ...f, name: editForm.name.trim(), quantity: editForm.qty, unit: editForm.unit, category: editForm.category, cal: Number(editForm.cal) || 0, protein: Number(editForm.protein) || 0, fat: Number(editForm.fat) || 0, carb: Number(editForm.carb) || 0 }
        : f
      ));
      setEditFood(null);
    } catch { /* keep modal open */ }
    finally { setEditSaving(false); }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    setOpenMenu(null);
    try {
      await foodsApi.remove(id);
      setFoods(prev => prev.filter(f => f.id !== id));
    } catch { /* silently fail */ }
    finally { setDeletingId(null); }
  };

  const subtitleMap: Record<Tab, string> = {
    import:  "Bulk import foods via Excel",
    library: `${foods.length} foods in library`,
    manual:  "Add a single food entry",
  };

  return (
    <>
      <DietitianTopbar title="Food &amp; Supplements" subtitle={subtitleMap[tab]} />

      <div className={classes.content}>

        {/* ── Tabs ── */}
        <div className={classes.tabs}>
          {([["import", "Import Excel", IcoUpload], ["library", "Food Library", IcoList], ["manual", "Add Manually", IcoPlus]] as const).map(([id, label, Icon]) => (
            <button key={id} className={`${classes.tab} ${tab === id ? classes.tab_active : ""}`} onClick={() => setTab(id)}>
              <Icon />{label}
            </button>
          ))}
        </div>

        {/* ══ IMPORT TAB ══════════════════════════════════════ */}
        {tab === "import" && (
          <div className={classes.section}>
            {/* Drop zone */}
            <div className={classes.drop_zone} onDragOver={e => e.preventDefault()} onDrop={handleDrop} onClick={() => fileRef.current?.click()}>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className={classes.file_input} onChange={handleFile} />
              <div className={classes.drop_icon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              </div>
              {fileName
                ? <p className={classes.drop_label}><strong>{fileName}</strong> — {rows.length} rows ready</p>
                : <p className={classes.drop_label}>Click or drag &amp; drop your <strong>.xlsx</strong> file here</p>}
              <p className={classes.drop_hint}>Columns: Food Name · Category · Quantity · Unit · Calories · Protein · Fat · Carbs</p>
            </div>

            {importErr  && <p className={classes.error_msg}>{importErr}</p>}
            {importDone && <div className={classes.success_msg}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg> Foods imported successfully.</div>}

            {/* Preview */}
            {rows.length > 0 && (
              <>
                <div className={classes.table_wrap}>
                  <table className={classes.table}>
                    <thead><tr><th>Food Name</th><th>Category</th><th>Quantity</th><th>Unit</th><th>Calories</th><th>Protein (g)</th><th>Fat (g)</th><th>Carbs (g)</th></tr></thead>
                    <tbody>{rows.map((r, i) => {
                      const cc = CAT_COLORS[r.category] ?? CAT_COLORS["Other"];
                      return (<tr key={i}><td>{r.name}</td><td><span className={classes.cat_badge}><span className={classes.cat_dot_sm} style={{ background: cc.color }}/>{r.category || "Other"}</span></td><td>{r.quantity || "—"}</td><td>{r.unit || "—"}</td><td>{r.calories}</td><td>{r.protein}</td><td>{r.fat}</td><td>{r.carbs}</td></tr>);
                    })}</tbody>
                  </table>
                </div>
                <div className={classes.actions}>
                  {uploading && <div className={classes.progress_wrap}><div className={classes.progress_bar} style={{ width: `${progress}%` }} /></div>}
                  <button className={classes.primary_btn} onClick={handleUpload} disabled={uploading}>
                    {uploading ? <><span className={classes.spinner} /> Importing {progress}%</> : `Import ${rows.length} Foods`}
                  </button>
                  <button className={classes.ghost_btn} onClick={handleClear} disabled={uploading}>Clear</button>
                </div>
              </>
            )}

            {/* Import history */}
            <div className={classes.section_header}>
              <h2 className={classes.section_title}>Import History</h2>
              <span className={classes.section_count}>{history.length} records</span>
            </div>
            <div className={classes.table_wrap}>
              {histLoading ? (
                <div className={classes.empty}><span className={classes.spinner_dark} /></div>
              ) : history.length === 0 ? (
                <div className={classes.empty}>No imports yet.</div>
              ) : (
                <table className={classes.table}>
                  <thead><tr><th>File Name</th><th>Foods Imported</th><th>Imported By</th><th>Date &amp; Time</th></tr></thead>
                  <tbody>
                    {history.map(h => (
                      <tr key={h.id}>
                        <td><span className={classes.file_badge}>{h.fileName}</span></td>
                        <td><span className={classes.count_badge}>{h.count}</span></td>
                        <td>{h.importedBy}</td>
                        <td>{fmtDate(h.importedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ══ LIBRARY TAB ═════════════════════════════════════ */}
        {tab === "library" && (
          <div className={classes.section}>
            <div className={classes.toolbar}>
              <div className={classes.search_wrap}>
                <svg className={classes.search_icon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input className={classes.search_input} placeholder="Search foods…" value={search} onChange={e => handleSearch(e.target.value)} />
              </div>
              <CatDropdown value={catFilter} onChange={handleCatFilter} />
            </div>

            {foodsErr && <p className={classes.error_msg}>{foodsErr}</p>}

            <div className={classes.table_wrap}>
              {foodsLoad ? (
                <div className={classes.empty}><span className={classes.spinner_dark} /></div>
              ) : paginated.length === 0 ? (
                <div className={classes.empty}>{search ? "No foods match your search." : "No foods in the library yet."}</div>
              ) : (
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Food Name</th>
                      <th>Category</th>
                      <th>Quantity</th>
                      <th>Calories</th>
                      <th>Protein (g)</th>
                      <th>Fat (g)</th>
                      <th>Carbs (g)</th>
                      <th>Usage</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((f, i) => (
                      <tr key={f.id}>
                        <td className={classes.row_num}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                        <td><strong>{f.name}</strong></td>
                        <td>{(() => { const cat = f.category ?? "Other"; const cc = CAT_COLORS[cat] ?? CAT_COLORS["Other"]; return <span className={classes.cat_badge}><span className={classes.cat_dot_sm} style={{ background: cc.color }}/>{cat}</span>; })()}</td>
                        <td>{f.quantity ? `${f.quantity}${f.unit ? ` ${f.unit}` : ""}` : "—"}</td>
                        <td><span className={classes.cal_badge}>{f.cal} kcal</span></td>
                        <td>{f.protein}g</td>
                        <td>{f.fat}g</td>
                        <td>{f.carb}g</td>
                        <td><span className={classes.usage_badge}>{f.usageCount ?? 0}×</span></td>
                        <td>
                          <div className={classes.kebab_wrap}>
                            <button
                              className={classes.kebab_btn}
                              onClick={e => {
                                e.stopPropagation();
                                if (openMenu === f.id) { setOpenMenu(null); setMenuAnchor(null); return; }
                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                setMenuAnchor({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
                                setOpenMenu(f.id);
                              }}
                              disabled={deletingId === f.id}
                            >
                              <span/><span/><span/>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {filtered.length > 0 && (
              <div className={classes.pagination}>
                <button className={classes.page_btn} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                {totalPages > 1 && Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                  .reduce<(number | "…")[]>((acc, n, idx, arr) => {
                    if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push("…");
                    acc.push(n);
                    return acc;
                  }, [])
                  .map((n, i) => n === "…"
                    ? <span key={`e${i}`} className={classes.page_ellipsis}>…</span>
                    : <button key={n} className={`${classes.page_btn} ${page === n ? classes.page_btn_active : ""}`} onClick={() => setPage(n as number)}>{n}</button>
                  )}
                <button className={classes.page_btn} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
                <span className={classes.page_info}>
                  {filtered.length <= PAGE_SIZE
                    ? `${filtered.length} food${filtered.length !== 1 ? "s" : ""}`
                    : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length}`}
                </span>
              </div>
            )}
          </div>
        )}

        {/* ══ MANUAL ADD TAB ══════════════════════════════════ */}
        {tab === "manual" && (
          <div className={classes.section}>
            <div className={classes.section_header}>
              <h2 className={classes.section_title}>Add Food Manually</h2>
            </div>

            {saveOk  && <div className={classes.success_msg}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg> Food added to the library.</div>}
            {saveErr && <p className={classes.error_msg}>{saveErr}</p>}

            <form className={classes.manual_form} onSubmit={handleManualAdd}>
              <div className={classes.form_grid}>
                <div className={classes.form_field}>
                  <label className={classes.label}>Food Name *</label>
                  <input className={classes.input} placeholder="e.g. Grilled Chicken Breast" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className={classes.form_field}>
                  <label className={classes.label}>Quantity / Serving</label>
                  <div className={classes.qty_row}>
                    <input className={classes.input} type="number" min="0" placeholder="100" value={form.qty} onChange={e => setForm(f => ({ ...f, qty: e.target.value }))} />
                    <select className={classes.unit_select} value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
                      <option value="g">g</option>
                      <option value="mg">mg</option>
                      <option value="kg">kg</option>
                      <option value="ml">ml</option>
                      <option value="L">L</option>
                      <option value="cups">cups</option>
                      <option value="tbsp">tbsp</option>
                      <option value="tsp">tsp</option>
                      <option value="oz">oz</option>
                      <option value="lb">lb</option>
                      <option value="piece">piece</option>
                      <option value="serving">serving</option>
                    </select>
                  </div>
                </div>
                <div className={classes.form_field}>
                  <label className={classes.label}>Calories (kcal)</label>
                  <input className={classes.input} type="number" min="0" placeholder="0" value={form.calories} onChange={e => setForm(f => ({ ...f, calories: e.target.value }))} />
                </div>
                <div className={classes.form_field}>
                  <label className={classes.label}>Protein (g)</label>
                  <input className={classes.input} type="number" min="0" placeholder="0" value={form.protein} onChange={e => setForm(f => ({ ...f, protein: e.target.value }))} />
                </div>
                <div className={classes.form_field}>
                  <label className={classes.label}>Fat (g)</label>
                  <input className={classes.input} type="number" min="0" placeholder="0" value={form.fat} onChange={e => setForm(f => ({ ...f, fat: e.target.value }))} />
                </div>
                <div className={classes.form_field}>
                  <label className={classes.label}>Carbs (g)</label>
                  <input className={classes.input} type="number" min="0" placeholder="0" value={form.carbs} onChange={e => setForm(f => ({ ...f, carbs: e.target.value }))} />
                </div>
                <div className={classes.form_field}>
                  <label className={classes.label}>Category</label>
                  <select className={classes.input} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className={classes.actions}>
                <button type="submit" className={classes.primary_btn} disabled={saving || !form.name.trim()}>
                  {saving ? <><span className={classes.spinner} /> Saving…</> : "Add to Library"}
                </button>
                <button type="button" className={classes.ghost_btn} onClick={() => { setForm(emptyForm); setSaveOk(false); setSaveErr(""); }}>Reset</button>
              </div>
            </form>
          </div>
        )}

      </div>

      {/* ── Kebab dropdown (fixed, outside overflow:hidden) ── */}
      {openMenu && menuAnchor && (
        <div
          className={classes.kebab_menu}
          style={{ position: "fixed", top: menuAnchor.top, right: menuAnchor.right, zIndex: 300 }}
          onClick={e => e.stopPropagation()}
        >
          <button className={classes.kebab_item} onClick={() => { const food = foods.find(f => f.id === openMenu); if (food) openEdit(food); setOpenMenu(null); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Edit
          </button>
          <button className={`${classes.kebab_item} ${classes.kebab_danger}`} onClick={() => handleDelete(openMenu)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
            Delete
          </button>
        </div>
      )}

      {/* ── Edit modal ─────────────────────────────────── */}
      {editFood && (
        <div className={classes.modal_overlay} onClick={() => setEditFood(null)}>
          <div className={classes.modal_box} onClick={e => e.stopPropagation()}>
            <div className={classes.modal_header}>
              <h3 className={classes.modal_title}>Edit Food</h3>
              <button className={classes.modal_close} onClick={() => setEditFood(null)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form onSubmit={handleEditSave}>
              <div className={classes.form_body}>
                <div className={classes.form_field}>
                  <label className={classes.label}>Food Name *</label>
                  <input className={classes.input} value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className={classes.form_field}>
                  <label className={classes.label}>Quantity / Serving</label>
                  <div className={classes.qty_row}>
                    <input className={classes.input} type="number" min="0" placeholder="100" value={editForm.qty} onChange={e => setEditForm(f => ({ ...f, qty: e.target.value }))} />
                    <select className={classes.unit_select} value={editForm.unit} onChange={e => setEditForm(f => ({ ...f, unit: e.target.value }))}>
                      <option value="g">g</option><option value="mg">mg</option><option value="kg">kg</option>
                      <option value="ml">ml</option><option value="L">L</option><option value="cups">cups</option>
                      <option value="tbsp">tbsp</option><option value="tsp">tsp</option><option value="oz">oz</option>
                      <option value="lb">lb</option><option value="piece">piece</option><option value="serving">serving</option>
                    </select>
                  </div>
                </div>
                <div className={classes.form_field}>
                  <label className={classes.label}>Category</label>
                  <select className={classes.input} value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className={classes.form_row}>
                  <div className={classes.form_field}>
                    <label className={classes.label}>Calories (kcal)</label>
                    <input className={classes.input} type="number" min="0" value={editForm.cal} onChange={e => setEditForm(f => ({ ...f, cal: e.target.value }))} />
                  </div>
                  <div className={classes.form_field}>
                    <label className={classes.label}>Protein (g)</label>
                    <input className={classes.input} type="number" min="0" value={editForm.protein} onChange={e => setEditForm(f => ({ ...f, protein: e.target.value }))} />
                  </div>
                  <div className={classes.form_field}>
                    <label className={classes.label}>Fat (g)</label>
                    <input className={classes.input} type="number" min="0" value={editForm.fat} onChange={e => setEditForm(f => ({ ...f, fat: e.target.value }))} />
                  </div>
                  <div className={classes.form_field}>
                    <label className={classes.label}>Carbs (g)</label>
                    <input className={classes.input} type="number" min="0" value={editForm.carb} onChange={e => setEditForm(f => ({ ...f, carb: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className={classes.modal_actions}>
                <button type="button" className={classes.ghost_btn} onClick={() => setEditFood(null)}>Cancel</button>
                <button type="submit" className={classes.primary_btn} disabled={editSaving || !editForm.name.trim()}>
                  {editSaving ? <><span className={classes.spinner} /> Saving…</> : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FoodLibrary;
